// Pure, testable image-cleanup pipeline for turning a photographed floor plan
// into clean black lines on a (optionally) transparent background.
// No React, no network, no external deps — plain Canvas 2D math.
// Implements TZ-2.md §1.2. NO OpenCV/wasm by design.
//
// Performance note (§1.2): for live preview, run on a downscaled copy
// (max side ~1200px). Run full resolution only on "Apply".

export interface CleanupOptions {
  brightness: number;        // -80..80
  contrast: number;          // -80..80
  threshold: number | null;  // null = auto (Otsu); otherwise 0..255
  denoise: boolean;          // box-blur before thresholding
  thicken: number;           // 0..3 — dilation radius (line thickening)
  transparentWhite: boolean; // white -> alpha 0
  invert: boolean;           // dark/light inverted
}

export interface CleanupResult {
  canvas: HTMLCanvasElement;
  autoThreshold: number;     // threshold Otsu picked (for UI display)
}

export const DEFAULT_OPTIONS: CleanupOptions = {
  brightness: 0,
  contrast: 20,
  threshold: null,
  denoise: false,
  thicken: 1,
  transparentWhite: true,
  invert: false,
};

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// ─── Step 3 — Otsu threshold ─────────────────────────────────────────────────
// Auto threshold by maximizing between-class variance. ~30 lines, no deps.
export function otsuThreshold(gray: Uint8ClampedArray | Uint8Array | number[]): number {
  const hist = new Array<number>(256).fill(0);
  for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
  const N = gray.length;
  if (N === 0) return 128;

  let sum = 0;
  for (let t = 0; t < 256; t++) sum += t * hist[t];

  let sumB = 0;
  let wB = 0;
  let maxVar = 0;
  let threshold = 128;

  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = N - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) ** 2;
    if (between > maxVar) {
      maxVar = between;
      threshold = t;
    }
  }
  return threshold;
}

// ─── Step 1 — grayscale + brightness/contrast ────────────────────────────────
// Luma weights 0.299/0.587/0.114. Standard contrast formula.
// Reads RGBA `data`, writes a single-channel gray array of length w*h.
export function toGray(
  data: Uint8ClampedArray,
  brightness: number,
  contrast: number,
): Uint8ClampedArray {
  const cFactor = (259 * (contrast + 255)) / (255 * (259 - contrast));
  const out = new Uint8ClampedArray(data.length / 4);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    let g = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    g = cFactor * (g - 128) + 128 + brightness;
    out[p] = clamp(g, 0, 255);
  }
  return out;
}

// ─── Step 2 — denoise (optional) ─────────────────────────────────────────────
// Box-blur 3x3, single pass. Removes JPEG crumble so threshold doesn't fracture
// lines. Full gaussian unnecessary for binarization.
export function boxBlur3x3(gray: Uint8ClampedArray, w: number, h: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(gray.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0;
      let count = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= w) continue;
          sum += gray[yy * w + xx];
          count++;
        }
      }
      out[y * w + x] = Math.round(sum / count);
    }
  }
  return out;
}

// ─── Step 4 — dilation (line thickening) ─────────────────────────────────────
// Thin pencil lines fracture after binarization. Dilating DARK pixels (bin=0)
// by a Manhattan radius makes lines continuous. `bin` holds 0 (dark) / 255 (light).
// Returns a new binary array with dark regions grown by `radius`.
export function dilateDark(
  bin: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number,
): Uint8ClampedArray {
  if (radius <= 0) return bin;
  const out = new Uint8ClampedArray(bin.length);
  out.fill(255);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      // If any pixel within Manhattan `radius` is dark, this pixel becomes dark.
      let dark = false;
      for (let dy = -radius; dy <= radius && !dark; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        const remX = radius - Math.abs(dy);
        for (let dx = -remX; dx <= remX; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= w) continue;
          if (bin[yy * w + xx] === 0) { dark = true; break; }
        }
      }
      out[y * w + x] = dark ? 0 : 255;
    }
  }
  return out;
}

// ─── Pipeline ─────────────────────────────────────────────────────────────────
// Runs all 5 steps on a source canvas/image and returns a processed canvas.
// `maxSide` lets the caller cap working resolution for live preview.
export function processImage(
  source: HTMLCanvasElement | HTMLImageElement,
  opts: CleanupOptions,
  maxSide?: number,
): CleanupResult {
  const srcW = source instanceof HTMLImageElement ? (source.naturalWidth || source.width) : source.width;
  const srcH = source instanceof HTMLImageElement ? (source.naturalHeight || source.height) : source.height;

  // Optional downscale for preview performance.
  let w = srcW;
  let h = srcH;
  if (maxSide && Math.max(srcW, srcH) > maxSide) {
    const k = maxSide / Math.max(srcW, srcH);
    w = Math.max(1, Math.round(srcW * k));
    h = Math.max(1, Math.round(srcH * k));
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');
  ctx.drawImage(source, 0, 0, w, h);

  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  // Step 1 — grayscale + brightness/contrast
  const grayBase = toGray(data, opts.brightness, opts.contrast);

  // Step 2 — denoise (optional). Single assignment keeps buffer-type generic consistent.
  const gray = opts.denoise ? boxBlur3x3(grayBase, w, h) : grayBase;

  // Step 3 — threshold (Otsu auto or manual)
  const autoThreshold = otsuThreshold(gray);
  const thr = opts.threshold == null ? autoThreshold : opts.threshold;

  // Binarize: pixels darker than threshold become dark (0), else light (255).
  // `invert` flips which side becomes dark.
  const binBase = new Uint8ClampedArray(gray.length);
  for (let p = 0; p < gray.length; p++) {
    const isDark = gray[p] < thr;
    const dark = opts.invert ? !isDark : isDark;
    binBase[p] = dark ? 0 : 255;
  }

  // Step 4 — dilation (optional). Single assignment keeps the buffer-type
  // generic consistent across both branches (avoids TS2322 on newer lib types).
  const bin = opts.thicken > 0 ? dilateDark(binBase, w, h, opts.thicken) : binBase;

  // Step 5 — write back: R=G=B=bin; transparentWhite -> alpha 0 on light pixels.
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const v = bin[p];
    data[i] = v;
    data[i + 1] = v;
    data[i + 2] = v;
    data[i + 3] = opts.transparentWhite && v === 255 ? 0 : 255;
  }
  ctx.putImageData(imageData, 0, 0);

  return { canvas, autoThreshold };
}
